import cx from 'classnames'
import Styles from './TikTokPreview.module.scss'
import { TikTokIntegration } from 'api/clients/ExternalIntegrationsClient'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import { MediaObject } from 'types'
import { isVideo } from 'components/ImagePickerModal/utils'
import { ReactNode } from 'react'
import { ReelsVideoPlayer } from '../components/ReelsVideoPlayer/ReelsVideoPlayer'
import { useVideoProgress } from '../hooks/useVideoProgress'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Actions } from './Actions'
import { Footer } from './Footer'
import { TopActions } from './TopActions'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'

interface TikTokPreviewProps {
  integration?: TikTokIntegration
  mediaObjects?: MediaObject[]
  content?: string
  loading?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  showVideoControls?: boolean
  isActive?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  isGenerating?: boolean
  onClose?: () => void
  alwaysTruncateCaption?: boolean
  post?: ScheduledPostShow
}

export const TikTokPreview = ({
  integration,
  mediaObjects,
  content,
  loading,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  isActive = true,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  alwaysTruncateCaption = false,
  post,
}: TikTokPreviewProps) => {
  const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })
  const videoUrl = video ? mediaObjects[0].source_url : undefined
  const videoDuration = mediaObjects?.[0]?.duration

  const { videoRef, progress } = useVideoProgress({ videoUrl, duration: videoDuration })

  // username needed for ReelsVideoPlayer
  const { username } = integration ? extractProfileInfo(integration) : { username: 'user' }

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          channel={DesignChannel.TikTokPost}
          almanacFileKey={almanacFileKey as string}
          borderRadius={16}
          position={video ? 'top' : undefined}
          onClose={onClose}
          className={isGenerating ? Styles.disableHover : undefined}
          caption={content}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      <div className={cx(Styles.container, containerClassName)}>
        <div className={Styles.content}>
          {customPreviewContent || (
            <ReelsVideoPlayer
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              isActive={isActive}
              className={Styles.preview}
              dataTestId="tiktok-video"
              username={username}
              videoRef={videoRef}
              almanacFileKey={almanacFileKey as string}
              isGenerating={isGenerating}
            />
          )}
        </div>
        <Footer
          integration={integration}
          content={content}
          post={post}
          onEdit={onEdit}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
        />
        <TopActions />
        <Actions integration={integration} />
        <div className={Styles.loadingBar}>
          <div className={Styles.progress} style={{ width: `${progress}%` }} />
        </div>
      </div>
    </ConditionalWrapper>
  )
}
